import {
  ADMIN_SINISTRO_MAX_FILE_SIZE_BYTES,
  ADMIN_SINISTRO_MAX_FILE_SIZE_LABEL,
} from '../../minha-conta/pages/admin/sinistro/admin-sinistro-upload.constants';
import {
  AVISO_SINISTRO_MAX_FILE_SIZE_BYTES,
  AVISO_SINISTRO_MAX_FILE_SIZE_LABEL,
} from '../../minha-conta/pages/aviso-sinistro/sinistro/claim-upload.constants';

/** Limite habitual de imagens e PDF administrativos (produtos, logotipo, etc.). */
export const TWENTY_MB_BYTES = 20 * 1024 * 1024;

/** Chassi / NF (clientes, pedido público). */
export const CHASSI_NOTA_FIFTY_MB_BYTES = 50 * 1024 * 1024;

export interface FileUploadValidationRules {
  readonly maxBytesPerFile: number;
  readonly allowedMimeTypes: readonly string[];
  readonly invalidTypeMessage: string;
  readonly tooLargeMessage: string;
  /** Aceita arquivo com extensão `.pdf` quando `file.type` estiver vazio (ex.: alguns SO). */
  readonly allowPdfExtensionFallback?: boolean;
}

export const CLAIM_UPLOAD_ACCEPT_EXTENSIONS =
  '.jpg,.jpeg,.png,.pdf,.mp4,.webm,.mov,.avi,.mkv,.m4v,.ogv,.wmv,.3gp';

/** Mesma lista MIME usada nos fluxos de documentação de sinistro (cliente e admin). */
export const CLAIM_DOCUMENT_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/mov',
  /** MIME real do Safari/iOS para `.mov` (alias legado `video/mov` permanece). */
  'video/quicktime',
  'video/avi',
  'video/x-msvideo',
  'video/mkv',
  'video/x-matroska',
  'video/m4v',
  'video/x-m4v',
  'video/ogv',
  'video/ogg',
  'video/wmv',
  'video/x-ms-wmv',
  'video/3gp',
  'video/3gpp',
] as const;

export const CLAIM_DOCUMENT_INVALID_TYPE_MESSAGE =
  'Somente arquivos JPG, PNG, PDF e MP4 (ou vídeos permitidos) são aceitos.';

export const CLIENT_CLAIM_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: AVISO_SINISTRO_MAX_FILE_SIZE_BYTES,
  allowedMimeTypes: [...CLAIM_DOCUMENT_ALLOWED_MIME_TYPES],
  invalidTypeMessage: CLAIM_DOCUMENT_INVALID_TYPE_MESSAGE,
  tooLargeMessage: `O tamanho máximo é ${AVISO_SINISTRO_MAX_FILE_SIZE_LABEL}.`,
};

export const ADMIN_CLAIM_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: ADMIN_SINISTRO_MAX_FILE_SIZE_BYTES,
  allowedMimeTypes: [...CLAIM_DOCUMENT_ALLOWED_MIME_TYPES],
  invalidTypeMessage: CLAIM_DOCUMENT_INVALID_TYPE_MESSAGE,
  tooLargeMessage: `O tamanho máximo é ${ADMIN_SINISTRO_MAX_FILE_SIZE_LABEL}.`,
};

export const CHASSI_NOTA_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
] as const;

export const CHASSI_NOTA_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: CHASSI_NOTA_FIFTY_MB_BYTES,
  allowedMimeTypes: [...CHASSI_NOTA_ALLOWED_MIME_TYPES],
  invalidTypeMessage: 'Somente arquivos JPG, PNG ou PDF são permitidos.',
  tooLargeMessage: 'O tamanho máximo do arquivo é 50MB.',
};

export const CHASSI_NOTA_ACCEPT_EXTENSIONS = '.jpg,.jpeg,.png,.pdf';

/** Anexos do Assistente Wosi (NF, chassi, cotação). */
export const CHAT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

export const CHAT_ATTACHMENT_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const;

export const CHAT_ATTACHMENT_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: CHAT_ATTACHMENT_MAX_BYTES,
  allowedMimeTypes: [...CHAT_ATTACHMENT_ALLOWED_MIME_TYPES],
  invalidTypeMessage: 'Somente arquivos JPG, PNG, WEBP ou PDF são permitidos.',
  tooLargeMessage: 'O tamanho máximo do arquivo no chat é 10MB.',
  allowPdfExtensionFallback: true,
};

export const CHAT_ATTACHMENT_ACCEPT_EXTENSIONS = '.jpg,.jpeg,.png,.pdf,.webp';
export const CHAT_ATTACHMENT_MAX_COUNT = 4;

/** Acervo administrativo — documento exemplo. */
export const ACERVO_SAMPLE_DOCUMENT_RULES: FileUploadValidationRules = {
  maxBytesPerFile: ADMIN_SINISTRO_MAX_FILE_SIZE_BYTES,
  allowedMimeTypes: [...CHASSI_NOTA_ALLOWED_MIME_TYPES],
  invalidTypeMessage: 'Somente arquivos JPG, PNG ou PDF são permitidos.',
  tooLargeMessage: `O tamanho máximo do arquivo é ${ADMIN_SINISTRO_MAX_FILE_SIZE_LABEL}.`,
};

export const COVERAGE_IMAGE_ACCEPT_EXTENSIONS = '.png,.svg';

export const COVERAGE_IMAGE_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/svg+xml',
] as const;

export const COVERAGE_IMAGE_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: TWENTY_MB_BYTES,
  allowedMimeTypes: [...COVERAGE_IMAGE_ALLOWED_MIME_TYPES],
  invalidTypeMessage: 'Somente imagens PNG e SVG são permitidas.',
  tooLargeMessage: 'O tamanho máximo da imagem é 20MB.',
};

export const PRODUCT_IMAGE_ACCEPT = 'image/*';

export const PRODUCT_IMAGE_ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/jpg',
  'image/gif',
] as const;

export const PRODUCT_IMAGE_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: TWENTY_MB_BYTES,
  allowedMimeTypes: [...PRODUCT_IMAGE_ALLOWED_MIME_TYPES],
  invalidTypeMessage:
    'Somente imagens PNG, JPEG, WEBP, JPG e GIF são permitidas.',
  tooLargeMessage: 'O tamanho máximo da imagem é 20MB.',
};

/** Minha página (banner/foto sem validação extra de dimensão no picker). */
export const PARTNER_PAGE_IMAGE_UPLOAD_RULES: FileUploadValidationRules =
  PRODUCT_IMAGE_UPLOAD_RULES;

export const GENERAL_CONDITIONS_PDF_ACCEPT = '.pdf,application/pdf';

export const PARTNER_HELP_MAX_BYTES = 500 * 1024 * 1024;

export const PARTNER_HELP_ACCEPT_EXTENSIONS =
  '.pdf,.jpg,.jpeg,.png,.webp,.mp4,.webm,.mov,.zip';

export const PARTNER_HELP_UPLOAD_RULES: FileUploadValidationRules = {
  maxBytesPerFile: PARTNER_HELP_MAX_BYTES,
  allowedMimeTypes: [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/webp',
    'video/mp4',
    'video/webm',
    'video/quicktime',
    'video/mov',
    'application/zip',
    'application/x-zip-compressed',
  ],
  invalidTypeMessage: 'Use PDF, imagem, vídeo MP4, WEBM, MOV ou ZIP.',
  tooLargeMessage: 'O tamanho máximo do arquivo é 500MB.',
  allowPdfExtensionFallback: true,
};

export const GENERAL_CONDITIONS_PDF_RULES: FileUploadValidationRules = {
  maxBytesPerFile: TWENTY_MB_BYTES,
  allowedMimeTypes: ['application/pdf'],
  invalidTypeMessage: 'Somente arquivos PDF são permitidos.',
  tooLargeMessage: 'O tamanho máximo do arquivo é 20MB.',
  allowPdfExtensionFallback: true,
};
